import {
  Cog6ToothIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-hot-toast";
import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  fetchCompany,
} from "../../../api/company";
import { useTranslation } from "react-i18next";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { PMField } from "../../../components/utils/PMField";
import PMListboxFormik from "../../../components/utils/PMListboxFormik";
import PMSwitch from "../../../components/utils/PMSwitch";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { FormikSaveBar } from "../../../components/utils/FormikSaveBar";
import { ProjectSelector } from "../../../components/utils/moduleSelectors/projectSelector";
import { updateCompanyAsync } from "../../../slices/company";
import { PMImagePicker } from "../../../components/utils/PMImagePicker";
import { PaymentTypeSelector } from "../../../components/utils/moduleSelectors/paymentTypeSelector";
import { ProductSelector } from "../../../components/utils/Selector/productSelector";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { initializeHseModules } from "../../../api/company";
import { fetchCompanyAiMailboxes } from "../../../api/microsoft";
import { PMTabs } from "../../../components/utils/PMTabs";
import {
  DEFAULT_PROMANG_RICH_TEXT_CONTEXT_MENU,
  PromangRichTextEditor,
} from "../../../components/utils/PromangRichTextEditor";
import CompanyEmployeePositionsEditor from "../../../components/dashboard/company/CompanyEmployeePositionsEditor";
import {
  InboundEmailSettingsSection,
  normalizeInboundEmailSettings,
} from "../../../components/dashboard/company/InboundEmailSettingsSection";

const COMPANY_SETTINGS_TAB_KEYS = ["company", "employment_agreement", "employee_positions"];

export const CompanySettings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [company, setCompany] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [, setErrors] = useState([]);
  const [, setError] = useState(null);
  const [hseInitializing, setHseInitializing] = useState(false);
  const [aiMailboxes, setAiMailboxes] = useState([]);
  const selectedSettingsTab = useMemo(() => {
    const tab = searchParams.get("tab");
    const index = COMPANY_SETTINGS_TAB_KEYS.indexOf(tab);
    return index >= 0 ? index : 0;
  }, [searchParams]);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const handleSettingsTabChange = (value) => {
    const index = Number(value);
    const tabKey = COMPANY_SETTINGS_TAB_KEYS[index] || COMPANY_SETTINGS_TAB_KEYS[0];
    setSearchParams({ tab: tabKey }, { replace: true });
  };

  const getCompany = () => {
    setIsLoading(true);
    fetchCompany()
      .then((res) => {
        // Set companyState
        setIsLoading(false);
        setCompany(res.data);
      })
      .catch((error) => {
        // Set companyState (errors, error and isLoading)
        setError(true);
        setErrors(error.data.errors);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getCompany();
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchCompanyAiMailboxes()
      .then((res) => {
        if (cancelled) return;
        const list = res?.ai_mailboxes;
        setAiMailboxes(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setAiMailboxes([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onRefresh = (data) => {
    // Update companyState
    setCompany(data);
  };

  const handleInitializeHseModules = async () => {
    if (
      window.confirm(
        t(
          "Er du sikker på at du vil initialisere HMS-moduler? Dette vil opprette standard verneområder og sjekkpunkter."
        )
      )
    ) {
      setHseInitializing(true);
      try {
        await initializeHseModules();
        toast.success(t("HMS-moduler initialisert successfully!"));
      } catch (err) {
        console.error("Error initializing HSE modules:", err);
        toast.error(t("Feil ved initialisering av HMS-moduler"));
      } finally {
        setHseInitializing(false);
      }
    }
  };

  const headerActions = [
    // {
    //   component: null,
    //   label: "Sync Tripletex with promang",
    //   description: "",
    //   path: null,
    //   action: () => setSyncDialogOpen(true),
    //   icon: ArrowPathRoundedSquareIcon,
    //   textColor: "text-teal-700",
    //   bgColor: "bg-white",
    //   hidden: false,
    //   menu: true,
    // },
  ];

  function Content() {
    return (
      <>
        <div className="col-span-3">
          <Formik
            initialValues={{
              name: company?.name,
              vat: company?.vat,
              mobile: company?.mobile,
              email: company?.email,
              web: company?.web,
              phone: company?.phone,

              sales_conditions_url: company?.sales_conditions_url,
              customer_review_url: company?.customer_review_url,

              mileage_minimum_price: company?.mileage_minimum_price,
              mileage_fixed_price: company?.mileage_fixed_price,

              default_webshop_order_project_id:
                company?.default_webshop_order_project_id,

              postal_address: {
                address: company?.postal_address?.address,
                address2: company?.postal_address?.address2,
                postal: company?.postal_address?.postal,
                place: company?.postal_address?.place,
              },
              physical_address: {
                address: company?.physical_address?.address,
                address2: company?.physical_address?.address2,
                postal: company?.physical_address?.postal,
                place: company?.physical_address?.place,
              },

              document_logo_id: company?.logo?.id,
              svv_automatic_api_update:
                company?.svv_automatic_api_update || false,

              outbound_company_ai_mailbox_id:
                company?.outbound_company_ai_mailbox_id ?? null,
              inbound_email_settings: normalizeInboundEmailSettings(
                company?.inbound_email_settings
              ),
              employment_agreement_description:
                company?.employment_agreement_description || "",

              submit: null,
            }}
            enableReinitialize
            validationSchema={Yup.object({
              name: Yup.string().max(255).required(t("customer.name.required")),
              description: Yup.string(),
            })}
            onSubmit={(values, helpers) => {
              try {
                toast.promise(dispatch(updateCompanyAsync(values)), {
                  loading: t("Saving...."),
                  success: (data) => {
                    if (data.status === 500) throw new Error("Server error");
                    onRefresh(data?.payload?.data?.data);
                    return t("Data was saved!");
                  },
                  error: t("Something went wrong. Could not save data!"),
                });
                helpers.setStatus({ success: true });
                helpers.setSubmitting(false);
              } catch (err) {
                toast.error("Nope");
                console.error(err);
                helpers.setStatus({ success: false });
                helpers.setErrors({ submit: err.message });
                helpers.setSubmitting(false);
              }
            }}
          >
            {(formik) => (
              <Form className="grid-span-1 sm:grid-span-2 w-full">
                <div className="mb-6">
                  <PMTabs
                    tabs={[
                      { name: t("company.settings.tabs.company") },
                      { name: t("company.settings.tabs.employment_agreement") },
                      { name: t("company.settings.tabs.employee_positions") },
                    ]}
                    selectedTab={selectedSettingsTab}
                    setSelectedTab={handleSettingsTabChange}
                  />
                </div>

                {selectedSettingsTab === 0 && (
                <div className="grid grid-cols-3 gap-6">
                  <div className="col-span-1 sm:col-span-2">
                    <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
                      <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                        {/* <div>
                    <h3 className="text-lg font-medium leading-6 text-gray-900">{ t("Product Description") }</h3>
                    <p className="mt-1 text-sm text-gray-500"></p>
                  </div> */}
                        <div className="grid sm:grid-cols-8 gap-6">
                          <div className="sm:col-span-2">
                            {/* <PMSwitch label="Active" name="active" {...formik} /> */}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div id="guide-company-profile" className="scroll-mt-24 mt-5 shadow sm:overflow-hidden sm:rounded-md">
                      <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                        <div>
                          <h3 className="text-lg font-medium leading-6 text-gray-900">
                            {t("Company Details")}
                          </h3>
                          <p className="mt-1 text-sm text-gray-500"></p>
                        </div>

                        <div className="grid grid-cols-6 gap-6">
                          <div className="col-span-6 sm:col-span-3">
                            <PMField
                              name="name"
                              type="text"
                              label="Company Name"
                              {...formik}
                            />
                          </div>
                          <div className="col-span-6 sm:col-span-3">
                            <PMField
                              name="vat"
                              type="text"
                              label="Vat"
                              {...formik}
                            />
                          </div>

                          <div className="col-span-6 sm:col-span-3 lg:col-span-2"></div>
                        </div>
                      </div>
                    </div>

                    <div id="guide-company-contact" className="scroll-mt-24 mt-5 shadow sm:overflow-hidden sm:rounded-md">
                      <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                        <div>
                          <h3 className="text-lg font-medium leading-6 text-gray-900">
                            {t("Contact information")}
                          </h3>
                          <p className="mt-1 text-sm text-gray-500"></p>
                        </div>

                        <div className="grid grid-cols-6 gap-6">
                          <div className="col-span-6 sm:col-span-3">
                            <PMField
                              name="mobile"
                              type="text"
                              label="Mobile"
                              {...formik}
                            />
                          </div>

                          <div className="col-span-6 sm:col-span-3">
                            <PMField
                              name="phone"
                              type="text"
                              label="Phone"
                              {...formik}
                            />
                          </div>

                          <div className="col-span-6 sm:col-span-3">
                            <PMField
                              name="email"
                              type="text"
                              label="Email"
                              {...formik}
                            />
                          </div>

                          <div className="col-span-6 sm:col-span-3 lg:col-span-2"></div>
                        </div>
                      </div>
                    </div>

                    {/* overflow-visible so Headless UI Listbox panel is not clipped by rounded card */}
                    <div className="mt-5 overflow-visible rounded-md shadow">
                      <div className="space-y-6 rounded-md bg-white py-6 px-4 sm:p-6">
                        <div>
                          <h3 className="text-lg font-medium leading-6 text-gray-900">
                            {t("company.outbound_mail.title")}
                          </h3>
                          <p className="mt-1 text-sm text-gray-500">
                            {t("company.outbound_mail.description")}
                          </p>
                        </div>
                        <div className="grid grid-cols-6 gap-6">
                          <div className="col-span-6 sm:col-span-6">
                            <PMListboxFormik
                              label={t("company.outbound_mail.mailbox_label")}
                              name="outbound_company_ai_mailbox_id"
                              selected={formik.values.outbound_company_ai_mailbox_id}
                              options={[
                                {
                                  value: null,
                                  label: t("company.outbound_mail.promang_default"),
                                },
                                ...aiMailboxes
                                  .filter((m) => m.microsoft_connected)
                                  .map((m) => ({
                                    value: m.id,
                                    label: m.email_address,
                                  })),
                              ]}
                              {...formik}
                            />
                            <p className="mt-2 text-sm text-gray-500">
                              {t("company.outbound_mail.case_email_hint")}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <InboundEmailSettingsSection formik={formik} />

                    <div id="guide-company-links" className="scroll-mt-24 mt-5 shadow sm:overflow-hidden sm:rounded-md">
                      <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                        <div>
                          <h3 className="text-lg font-medium leading-6 text-gray-900">
                            {t("Company Links")}
                          </h3>
                          <p className="mt-1 text-sm text-gray-500"></p>
                        </div>

                        <div className="grid grid-cols-6 gap-6">
                          <div className="col-span-6 sm:col-span-6">
                            <PMField
                              name="sales_conditions_url"
                              type="text"
                              label="Sales Conditions URL"
                              {...formik}
                            />
                          </div>

                          <div className="col-span-6 sm:col-span-3 lg:col-span-2"></div>
                        </div>

                        <div className="grid grid-cols-6 gap-6">
                          <div className="col-span-6 sm:col-span-6">
                            <PMField
                              name="customer_review_url"
                              type="text"
                              label="Customer Review URL"
                              {...formik}
                            />
                          </div>

                          <div className="col-span-6 sm:col-span-3 lg:col-span-2"></div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
                      <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                        <div>
                          <h3 className="text-lg font-medium leading-6 text-gray-900">
                            {t("Bilgodtgjørelse")}
                          </h3>
                          <p className="mt-1 text-sm text-gray-500"></p>
                        </div>

                        <div className="grid grid-cols-6 gap-6">
                          <div className="col-span-6 sm:col-span-6">
                            <PMField
                              name="mileage_minimum_price"
                              type="text"
                              label="Mileage Minimum Price"
                              {...formik}
                            />
                          </div>

                          <div className="col-span-6 sm:col-span-3 lg:col-span-2"></div>
                        </div>

                        <div className="grid grid-cols-6 gap-6">
                          <div className="col-span-6 sm:col-span-6">
                            <PMField
                              name="mileage_fixed_price"
                              type="text"
                              label="Mileage Fixed Price"
                              {...formik}
                            />
                          </div>

                          <div className="col-span-6 sm:col-span-3 lg:col-span-2"></div>
                        </div>
                      </div>
                    </div>

                    <div id="guide-company-addresses" className="scroll-mt-24 mt-5 shadow sm:overflow-hidden sm:rounded-md">
                      <div className="grid sm:grid-cols-2 gap-6">
                        <div className="grid grid-cols-3 sm:grid-cols-3 bg-white sm:p-6 gap-6">
                          <div className="col-span-3">
                            <h3 className="text-lg font-medium leading-6 text-gray-900">
                              {t("Postal Address")}
                            </h3>
                            <p className="mt-1 text-sm text-gray-500"></p>
                          </div>
                          <div className="col-span-3 sm:col-span-3">
                            <PMField
                              name="postal_address.address"
                              type="text"
                              label="Address"
                              {...formik}
                            />
                          </div>
                          <div className="col-span-3 sm:col-span-3">
                            <PMField
                              name="postal_address.address2"
                              type="text"
                              label="Address 2"
                              {...formik}
                            />
                          </div>
                          <div className="col-span-1 sm:col-span-1">
                            <PMField
                              name="postal_address.postal"
                              type="text"
                              label="Postal"
                              placeName="postal_address.place"
                              {...formik}
                            />
                          </div>
                          <div className="col-span-2 sm:col-span-2">
                            <PMField
                              name="postal_address.place"
                              type="text"
                              label="Place"
                              {...formik}
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-3 sm:grid-cols-3 bg-white sm:p-6 gap-6">
                          <div className="col-span-3">
                            <h3 className="text-lg font-medium leading-6 text-gray-900">
                              {t("Physical Address")}
                            </h3>
                            <p className="mt-1 text-sm text-gray-500"></p>
                          </div>
                          <div className="col-span-3 sm:col-span-3">
                            <PMField
                              name="physical_address.address"
                              type="text"
                              label="Address"
                              {...formik}
                            />
                          </div>
                          <div className="col-span-3 sm:col-span-3">
                            <PMField
                              name="physical_address.address2"
                              type="text"
                              label="Address 2"
                              {...formik}
                            />
                          </div>
                          <div className="col-span-1 sm:col-span-1">
                            <PMField
                              name="physical_address.postal"
                              type="text"
                              label="Postal"
                              {...formik}
                            />
                          </div>
                          <div className="col-span-2 sm:col-span-2">
                            <PMField
                              name="physical_address.place"
                              type="text"
                              label="Place"
                              {...formik}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-span-1">
                    <div className="col-span-1 sm:col-span-2">
                      <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
                        <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                          {/* <div>
                    <h3 className="text-lg font-medium leading-6 text-gray-900">{ t("Product Description") }</h3>
                    <p className="mt-1 text-sm text-gray-500"></p>
                  </div> */}
                          <div className="grid sm:grid-cols-8 gap-6">
                            <div className="sm:col-span-2">
                              {/* <PMSwitch label="Active" name="active" {...formik} /> */}
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* logo upload start */}
                      <div id="guide-company-logo" className="scroll-mt-24 mt-5 shadow sm:overflow-hidden sm:rounded-md">
                        <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                          <div className="grid grid-cols-6 gap-6">
                            <div className="col-span-6 sm:col-span-6">
                              <PMImagePicker
                                label="Logo"
                                selected={company?.logo}
                                name="document_logo_id"
                                {...formik}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* logo upload end */}
                      {/* default_webshop_order_project_id start */}
                      <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
                        <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                          <div className="grid grid-cols-6 gap-6">
                            <div className="col-span-6 sm:col-span-6">
                              <ProjectSelector
                                label="Default Webshop Order Project"
                                name="default_webshop_order_project_id"
                                selected={
                                  company?.default_webshop_order_project?.id
                                    ? company?.default_webshop_order_project
                                    : null
                                }
                                {...formik}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* default_webshop_order_project_id end */}
                      {/* default_webshop_order_project_id start */}
                      {company?.stripe_webhook_secret && (
                        <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
                          <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                            <div className="grid grid-cols-6 gap-6">
                              <div className="col-span-6 sm:col-span-6">
                                <PaymentTypeSelector
                                  label="Stripe Payment Type"
                                  name="stripe_payment_type_id"
                                  selected={
                                    company?.stripe_payment_type?.id
                                      ? company?.stripe_payment_type
                                      : null
                                  }
                                  {...formik}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {/* default_webshop_order_project_id end */}

                      {/* car_allowance_product start */}
                      {company?.car_allowance_product && (
                        <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
                          <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                            <div className="grid grid-cols-6 gap-6">
                              <div className="col-span-6 sm:col-span-6">
                                <ProductSelector
                                  label="Car Allowance Product"
                                  name="car_allowance_product_id"
                                  selected={
                                    company?.car_allowance_product?.id
                                      ? company?.car_allowance_product
                                      : null
                                  }
                                  {...formik}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {/* car_allowance_product end */}

                      {/* SVV API Settings */}
                      <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
                        <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                          <div>
                            <h3 className="text-lg font-medium leading-6 text-gray-900">
                              {t("Statens Vegvesen API")}
                            </h3>
                            <p className="mt-1 text-sm text-gray-500">
                              {t("Norwegian Vehicle Authority Integration")}
                            </p>
                          </div>
                          <div className="grid grid-cols-6 gap-6">
                            <div className="col-span-6 sm:col-span-6">
                              <PMSwitch
                                name="svv_automatic_api_update"
                                label={t("Enable Automatic SVV Data Updates")}
                                description={t(
                                  "Automatically update vehicle data from Statens Vegvesen API for all company vehicles"
                                )}
                                {...formik}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* SVV API Settings end */}

                      {/* HSE Modules Setup */}
                      <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
                        <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                          <div>
                            <h3 className="text-lg font-medium leading-6 text-gray-900">
                              {t("HMS-moduler")}
                            </h3>
                            <p className="mt-1 text-sm text-gray-500">
                              {t(
                                "Initialiser HMS-moduler og standard verneområder"
                              )}
                            </p>
                          </div>
                          <div className="grid grid-cols-6 gap-6">
                            <div className="col-span-6 sm:col-span-6">
                              <button
                                type="button"
                                onClick={handleInitializeHseModules}
                                disabled={hseInitializing}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {hseInitializing ? (
                                  <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    {t("Initialiserer...")}
                                  </>
                                ) : (
                                  <>
                                    <Cog6ToothIcon className="h-4 w-4 mr-2" />
                                    {t("Initialiser HMS-moduler")}
                                  </>
                                )}
                              </button>
                              <p className="mt-2 text-sm text-gray-500">
                                {t(
                                  "Dette vil opprette standard verneområder og sjekkpunkter for HMS-vernerunder"
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* HSE Modules Setup end */}
                    </div>
                  </div>
                </div>
                )}

                {selectedSettingsTab === 1 && (
                  <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
                    <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
                      <div>
                        <h3 className="text-lg font-medium leading-6 text-gray-900">
                          {t("company.employment_agreement.title")}
                        </h3>
                        <p className="mt-1 text-sm text-gray-500">
                          {t("company.employment_agreement.description")}
                        </p>
                      </div>
                      <div>
                        <PromangRichTextEditor
                          value={formik.values.employment_agreement_description || ""}
                          onChange={(html) =>
                            formik.setFieldValue("employment_agreement_description", html)
                          }
                          placeholder={t("company.employment_agreement.placeholder")}
                          size="lg"
                          contextMenu={DEFAULT_PROMANG_RICH_TEXT_CONTEXT_MENU}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {selectedSettingsTab === 2 && <CompanyEmployeePositionsEditor />}

                {/* <PromptIfDirty {...formik}/> */}
                <FormikSaveBar {...formik} />
              </Form>
            )}
          </Formik>
        </div>
      </>
    );
  }

  if (isLoading) {
    return <ResourceLoading />;
  }

  return (
    <>
      <DashboardHeader
        subtitle=""
        actions={headerActions}
        title={"Settings"}
        meta={[]}
        // mainButton={{
        //   label: "Edit",
        //   action: () => setReportDialogOpen(true),
        //   path: null,
        //   color: "",
        //   icon: PencilIcon,
        // }}
      >
        <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
          <Content />
        </div>
      </DashboardHeader>
    </>
  );
};
